import React, {
    useCallback, useContext, useEffect, useRef, useState,
} from "react";
import axios from "axios";
import { useLocation } from "@reach/router";
import { PatioContext } from "@root/context/patio-calculator";
import { MEASUREMENTS_STATE } from "@root/constants/enums";
import Button from "@_components/button";
import {
    buildScanLidarPath,
    getScanPayload,
    getScanPayloadFromSearch,
    isScanLidarPath,
} from "@helpers/scan-lidar";

const SCAN_MESSAGE = {
    START: "start_scan_engine_requested",
    STARTED: "scan_engine_started",
    FINISHED: "upload_scan_finished",
    EXIT: "scan_engine_exit",
};

const SCAN_STATE = {
    IDLE: "idle",
    SCANNING: "scanning",
    FINISHED: "finished",
    ERROR: "error",
};

const EXIT_REASON_MESSAGES = {
    user_cancelled: "Scan cancelled.",
    engine_failure: "The scan failed. Please try again or enter measurements manually.",
    device_unsupported: "3D scan is not supported on this device.",
    upload_failed: "Upload failed. Please try again.",
    upload_timeout: "Upload timed out. If measurements arrive shortly, they will be applied automatically.",
    invalid_cluster_id: "This product is not supported for scanning.",
};

const isCausewayAvailable = () => typeof window !== "undefined" && Boolean(window.causeway);

const getMessageName = (envelope) => envelope?.name || envelope?.payload?.process;

const METERS_TO_INCHES = 39.3701;
const SCAN_API_URL = process.env.GATSBY_SCAN_API_URL || "https://3d-engine.sealskincovers.com";

const toInches = (value, unit) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
        return null;
    }

    if (unit === "m") {
        return numeric * METERS_TO_INCHES;
    }

    if (unit === "in" || unit === "inch" || unit === "inches") {
        return numeric;
    }

    return numeric;
};

const toMeters = (value, unit) => {
    const inches = toInches(value, unit);
    if (inches == null) {
        return null;
    }

    return inches / METERS_TO_INCHES;
};

export const mapPatioToRealMeasurements = (
    scanMeasurements = {},
    patioMeasurements = {},
    configuration = {},
) => {
    if (!scanMeasurements || typeof scanMeasurements !== "object") {
        return null;
    }

    const mapped = {};

    Object.entries(scanMeasurements).forEach(([dimensionName, entry]) => {
        const productKey = entry?.key;
        let meters = null;

        if (productKey && patioMeasurements?.[productKey] && !patioMeasurements[productKey].default) {
            const inches = Number(configuration?.[productKey] ?? patioMeasurements[productKey].value);
            if (!Number.isNaN(inches)) {
                meters = inches / METERS_TO_INCHES;
            }
        }

        if (meters == null) {
            meters = toMeters(entry?.value, entry?.unit);
        }

        if (meters == null) {
            return;
        }

        mapped[dimensionName] = {
            unit: "m",
            value: meters,
        };
    });

    const expectedKeys = Object.keys(scanMeasurements);
    if (expectedKeys.length === 0 || expectedKeys.some((key) => !mapped[key])) {
        return null;
    }

    return mapped;
};

export const mapScanResultToMeasurements = (parsed, productMeasurements = []) => {
    if (!parsed?.measurements || !productMeasurements.length) {
        return {};
    }

    const productKeys = new Set(productMeasurements.map((m) => m.key));
    const mapped = {};

    Object.values(parsed.measurements).forEach((entry) => {
        const code = entry?.key;
        if (!code || !productKeys.has(code)) {
            return;
        }

        const inches = toInches(entry.value, entry.unit);
        if (inches == null) {
            return;
        }

        mapped[code] = Math.round(inches);
    });

    return mapped;
};

const CausewayBridge = ({ product, onScanComplete }) => {
    const {
        setMeasurement,
        measurements: patioMeasurements,
        configuration,
        measurementsState,
    } = useContext(PatioContext) || {};
    const [bridgeAvailable, setBridgeAvailable] = useState(false);
    const [scanState, setScanState] = useState(SCAN_STATE.IDLE);
    const [scanResult, setScanResult] = useState(null);
    const [scanError, setScanError] = useState(null);
    const [lastSent, setLastSent] = useState(null);
    const [submittingRealMeasurements, setSubmittingRealMeasurements] = useState(false);
    const [realMeasurementsFeedback, setRealMeasurementsFeedback] = useState(null);
    const activeScanRef = useRef(0);

    const location = useLocation();
    const searchParams = new URLSearchParams(location?.search || "");
    const isAppClip = searchParams.get("appclip") === "true";
    const scanPayload = {
        ...getScanPayload(product),
        ...getScanPayloadFromSearch(location?.search),
    };
    const clusterId = scanPayload.cluster_id || null;
    const scanLidarPath = buildScanLidarPath(product);
    const showScanLidarLink = !isScanLidarPath(location?.pathname);

    const applyScanResult = useCallback((parsed) => {
        const mapped = mapScanResultToMeasurements(parsed, product?.measurements);

        if (typeof setMeasurement === "function") {
            Object.entries(mapped).forEach(([key, value]) => {
                setMeasurement(key, value);
            });
        }

        if (typeof onScanComplete === "function") {
            onScanComplete({ raw: parsed, measurements: mapped });
        }

        return mapped;
    }, [onScanComplete, product?.measurements, setMeasurement]);

    useEffect(() => {
        if (!isCausewayAvailable()) {
            setBridgeAvailable(false);
            return undefined;
        }

        setBridgeAvailable(true);

        const previousHandler = window.causeway.onMessage;

        window.causeway.onMessage = (envelope) => {
            const messageName = getMessageName(envelope);
            const payload = envelope?.payload || {};

            if (messageName === SCAN_MESSAGE.STARTED) {
                setScanState(SCAN_STATE.SCANNING);
                setScanError(null);
            }

            if (messageName === SCAN_MESSAGE.FINISHED) {
                const scanId = activeScanRef.current;

                try {
                    const parsed = JSON.parse(payload.backend_response_json || "{}");
                    const mapped = applyScanResult(parsed);

                    setScanResult({
                        receivedAt: new Date().toISOString(),
                        clusterId: payload.cluster_id,
                        raw: parsed,
                        measurements: mapped,
                    });
                    setScanState(SCAN_STATE.FINISHED);
                    setScanError(null);
                } catch (error) {
                    if (scanId === activeScanRef.current) {
                        setScanState(SCAN_STATE.ERROR);
                        setScanError("Could not read scan results. Please enter measurements manually.");
                    }
                }
            }

            if (messageName === SCAN_MESSAGE.EXIT) {
                if (activeScanRef.current > 0) {
                    const reason = payload.reason || "engine_failure";
                    setScanState(SCAN_STATE.ERROR);
                    setScanError(EXIT_REASON_MESSAGES[reason] || "Scan ended unexpectedly.");
                }
            }

            if (typeof previousHandler === "function") {
                previousHandler(envelope);
            }
        };

        return () => {
            if (window.causeway) {
                window.causeway.onMessage = previousHandler;
            }
        };
    }, [applyScanResult]);

    const handleOpenScanLidar = () => {
        if (!scanLidarPath) {
            return;
        }

        window.open(scanLidarPath, "_blank", "noopener,noreferrer");
    };

    const handleStartScan = () => {
        if (!window.causeway || !clusterId) {
            return;
        }

        activeScanRef.current += 1;
        const payload = { ...scanPayload };

        window.causeway.postMessage(SCAN_MESSAGE.START, payload);

        setScanState(SCAN_STATE.SCANNING);
        setScanResult(null);
        setScanError(null);
        setRealMeasurementsFeedback(null);
        setLastSent({
            sentAt: new Date().toISOString(),
            name: SCAN_MESSAGE.START,
            payload,
        });
    };

    const scanId = scanResult?.raw?.scan_id;
    const realMeasurementsPayload = scanResult?.raw?.measurements
        ? mapPatioToRealMeasurements(
            scanResult.raw.measurements,
            patioMeasurements,
            configuration,
        )
        : null;

    const canSubmitRealMeasurements = Boolean(
        scanId
        && SCAN_API_URL
        && measurementsState === MEASUREMENTS_STATE.FINISHED
        && realMeasurementsPayload
        && !submittingRealMeasurements,
    );

    const handleSubmitRealMeasurements = async () => {
        if (!canSubmitRealMeasurements) {
            return;
        }

        setSubmittingRealMeasurements(true);
        setRealMeasurementsFeedback(null);

        try {
            const response = await axios.put(
                `${SCAN_API_URL}/v1/scans/${scanId}/real_measurements`,
                { measurements: realMeasurementsPayload },
            );

            setRealMeasurementsFeedback({
                type: "success",
                message: "Real measurements saved.",
                data: response.data,
            });
        } catch (error) {
            const status = error?.response?.status;
            const responseData = error?.response?.data;
            const apiMessage = responseData?.detail
                || responseData?.message
                || responseData?.error
                || (typeof responseData === "string" ? responseData : null)
                || error?.message;
            let message = apiMessage
                ? `Could not save real measurements: ${apiMessage}`
                : "Could not save real measurements. Please try again.";

            if (status === 404) {
                message = "Scan result is not ready yet. Please try again shortly.";
            } else if (status === 422) {
                message = "Measurements do not match the scan dimensions. Please review and try again.";
            }

            setRealMeasurementsFeedback({
                type: "error",
                message,
                data: responseData || null,
            });
        } finally {
            setSubmittingRealMeasurements(false);
        }
    };


    if (!bridgeAvailable) {
        if (!isAppClip) {
            return null;
        }

        if (!showScanLidarLink || !scanLidarPath) {
            return null;
        }

        return (
            <section className="causeway-bridge" aria-label="3D scan">
                <div className="causeway-bridge__actions">
                    <Button
                        variant="primary-sates"
                        className="causeway-bridge__button"
                        onClick={handleOpenScanLidar}
                    >
                        Open LiDAR scan
                    </Button>
                </div>
            </section>
        );
    }

    const isScanning = scanState === SCAN_STATE.SCANNING;

    return (
        <section className="causeway-bridge" aria-label="3D scan">
            <div className="causeway-bridge__actions">
                <Button
                    variant="primary-sates"
                    className="causeway-bridge__button"
                    onClick={handleStartScan}
                    disabled={!clusterId || isScanning}
                >
                    {isScanning ? "Scanning…" : "Scan with iPhone"}
                </Button>

                {scanId && (
                    <Button
                        variant="secondary"
                        className="causeway-bridge__button"
                        onClick={handleSubmitRealMeasurements}
                        disabled={!canSubmitRealMeasurements}
                    >
                        {submittingRealMeasurements ? "Saving measurements…" : "Submit real measurements"}
                    </Button>
                )}
            </div>

            {scanId && measurementsState !== MEASUREMENTS_STATE.FINISHED && (
                <p className="causeway-bridge__status">
                    Enter all measurements before submitting real dimensions.
                </p>
            )}

            {realMeasurementsFeedback && (
                <p className={`causeway-bridge__status${realMeasurementsFeedback.type === "error" ? " causeway-bridge__status--error" : ""}`}>
                    {realMeasurementsFeedback.message}
                </p>
            )}

            {!clusterId && (
                <p className="causeway-bridge__status causeway-bridge__status--error">
                    Scanning is not available for this product.
                </p>
            )}

            {scanError && (
                <p className="causeway-bridge__status causeway-bridge__status--error">
                    {scanError}
                </p>
            )}

            {/* {scanState === SCAN_STATE.FINISHED && scanResult && (
                <div className="causeway-bridge__panel">
                    <h3 className="causeway-bridge__heading">Measurements applied</h3>
                    <pre className="causeway-bridge__json">
                        {JSON.stringify(scanResult.measurements, null, 2)}
                    </pre>
                </div>
            )} */}

            {lastSent && process.env.NODE_ENV === "development" && (
                <div className="causeway-bridge__panel">
                    <h3 className="causeway-bridge__heading">Last sent</h3>
                    <pre className="causeway-bridge__json">
                        {JSON.stringify(lastSent, null, 2)}
                    </pre>
                </div>
            )}

            {realMeasurementsPayload && process.env.NODE_ENV === "development" && (
                <div className="causeway-bridge__panel">
                    <h3 className="causeway-bridge__heading">Real measurements payload</h3>
                    <pre className="causeway-bridge__json">
                        {JSON.stringify({ scan_id: scanId, measurements: realMeasurementsPayload }, null, 2)}
                    </pre>
                </div>
            )}
        </section>
    );
};

export { getClusterId } from "@helpers/scan-lidar";
export default CausewayBridge;
